import { useEffect, useState } from 'react';
import { Alert, Button, Card, Form } from 'react-bootstrap';
import { getIndexerErrorInfo } from '../../api/errors';
import {
  useCreateInspectScheduleMutation,
  usePatchOnboardingMutation,
} from '../../api/indexerApi';
import type { ScheduleMessenger } from '../../api/types';
import {
  DEFAULT_SCHEDULE_MESSENGERS,
  ScheduleChannelBotsAlert,
  ScheduleMessengerSelect,
  filterJoinedScheduleMessengers,
} from '../ScheduleMessengerSelect';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { InspectScheduleWebhookForm } from '../InspectScheduleWebhookForm';
import {
  ensureWebhookBucketFilters,
  hasInspectPerUrlWebhook,
} from '../../lib/inspect-webhook-events';
import {
  applyDefaultInspectScheduleSettings,
  RECOMMENDED_SETTINGS_ALERT,
  RECOMMENDED_SETTINGS_LABEL,
  type InspectScheduleSettingsMode,
} from '../../lib/inspect-schedule-settings';
import { formatFaNumber, parseUrlList } from '../../lib/utils';
import type { InspectScheduleCreatePrefill } from '../../lib/inspect-schedule-prefill';

export function OnboardingScheduleStep({
  projectId,
  prefill,
  onComplete,
}: {
  projectId: string;
  prefill: InspectScheduleCreatePrefill | null;
  onComplete: () => void;
}) {
  const defaults = applyDefaultInspectScheduleSettings();
  const [name, setName] = useState(prefill?.name ?? 'پیگیری وضعیت ایندکس');
  const [enabled, setEnabled] = useState(true);
  const [urlText, setUrlText] = useState((prefill?.urls ?? []).join('\n'));
  const [webhookFilters, setWebhookFilters] = useState<string[]>(defaults.filters);
  const [webhookEvents, setWebhookEvents] = useState<string[]>(defaults.events);
  const [autoIndexNotIndexed, setAutoIndexNotIndexed] = useState(
    defaults.autoIndexNotIndexed,
  );
  const [settingsMode, setSettingsMode] =
    useState<InspectScheduleSettingsMode>('default');
  const [messengers, setMessengers] = useState<ScheduleMessenger[]>(
    DEFAULT_SCHEDULE_MESSENGERS,
  );
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{
    variant: string;
    text: string;
  } | null>(null);
  const [createInspectSchedule] = useCreateInspectScheduleMutation();
  const [patchOnboarding] = usePatchOnboardingMutation();
  const { data: userInfo } = useGetUserInfoQuery();

  useEffect(() => {
    if (!prefill?.urls?.length) return;
    setUrlText(prefill.urls.join('\n'));
    if (prefill.name) setName(prefill.name);
  }, [prefill]);

  const urls = parseUrlList(urlText);

  async function onSave() {
    if (!name.trim() || urls.length === 0) {
      setMessage({ variant: 'warning', text: 'نام و حداقل یک URL لازم است.' });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const preset = applyDefaultInspectScheduleSettings();
      const eventsToSave =
        settingsMode === 'default' ? preset.events : webhookEvents;
      const autoIndexToSave =
        settingsMode === 'default'
          ? preset.autoIndexNotIndexed
          : autoIndexNotIndexed;
      const filtersToSave =
        settingsMode === 'default'
          ? preset.filters
          : hasInspectPerUrlWebhook(eventsToSave)
            ? ensureWebhookBucketFilters(webhookFilters)
            : [];
      await createInspectSchedule({
        projectId,
        name: name.trim(),
        enabled,
        urls,
        webhookFilters: filtersToSave,
        webhookEvents: eventsToSave,
        autoIndexNotIndexed: autoIndexToSave,
        settingsMode,
        messengers: filterJoinedScheduleMessengers(
          settingsMode === 'default'
            ? [...DEFAULT_SCHEDULE_MESSENGERS]
            : messengers,
          userInfo,
        ),
      }).unwrap();
      await patchOnboarding({ projectId, step: 6 }).unwrap();
      onComplete();
    } catch (err) {
      setMessage({
        variant: 'danger',
        text:
          getIndexerErrorInfo(err).message || 'ذخیره زمان‌بندی ناموفق بود',
      });
    } finally {
      setBusy(false);
    }
  }

  async function onSkip() {
    setBusy(true);
    try {
      await patchOnboarding({
        projectId,
        scheduleSkipped: true,
        step: 6,
      }).unwrap();
      onComplete();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="mb-3 border">
      <Card.Header className="bg-body-tertiary">
        <h2 className="h5 mb-0">زمان‌بندی پیگیری وضعیت ایندکس</h2>
      </Card.Header>
      <Card.Body>
        <p className="text-body mb-3 fs--1">
          برای همان آدرس‌هایی که در مرحله قبل برای ایندکس فرستادید، پیگیری
          وضعیت را زمان‌بندی کنید. می‌توانید این مرحله را «بعداً» بگذارید.
        </p>
        {message && (
          <Alert variant={message.variant} className="py-2 small">
            {message.text}
          </Alert>
        )}

        <Form.Group className="mb-3">
          <Form.Label>نام زمان‌بندی</Form.Label>
          <Form.Control
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Check
            type="checkbox"
            id="onboarding-schedule-enabled"
            label="فعال"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>
            آدرس‌ها ({formatFaNumber(urls.length)})
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={6}
            className="mono small"
            value={urlText}
            onChange={(e) => setUrlText(e.target.value)}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label className="mb-2">نوع تنظیمات</Form.Label>
          <div className="d-flex flex-wrap gap-3">
            <Form.Check
              type="radio"
              id="onboarding-schedule-settings-default"
              name="onboarding-schedule-settings-mode"
              label={RECOMMENDED_SETTINGS_LABEL}
              checked={settingsMode === 'default'}
              onChange={() => {
                const preset = applyDefaultInspectScheduleSettings();
                setSettingsMode('default');
                setWebhookEvents(preset.events);
                setWebhookFilters(preset.filters);
                setAutoIndexNotIndexed(preset.autoIndexNotIndexed);
                setMessengers([...DEFAULT_SCHEDULE_MESSENGERS]);
              }}
            />
            <Form.Check
              type="radio"
              id="onboarding-schedule-settings-advanced"
              name="onboarding-schedule-settings-mode"
              label="تنظیمات پیشرفته"
              checked={settingsMode === 'advanced'}
              onChange={() => setSettingsMode('advanced')}
            />
          </div>
          {settingsMode === 'default' && (
            <>
              <Alert variant="info" className="py-2 small mt-2 mb-0">
                {RECOMMENDED_SETTINGS_ALERT}
              </Alert>
              <ScheduleChannelBotsAlert className="mt-2 mb-0" />
            </>
          )}
        </Form.Group>
        {settingsMode === 'advanced' && (
          <>
            <InspectScheduleWebhookForm
              events={webhookEvents}
              filters={webhookFilters}
              autoIndexNotIndexed={autoIndexNotIndexed}
              onEventsChange={setWebhookEvents}
              onFiltersChange={setWebhookFilters}
              onAutoIndexNotIndexedChange={setAutoIndexNotIndexed}
            />
            <ScheduleMessengerSelect value={messengers} onChange={setMessengers} />
          </>
        )}
      </Card.Body>
      <Card.Footer className="bg-body-tertiary border-top d-flex flex-wrap gap-2 justify-content-end py-3">
        <Button
          variant="falcon-primary"
          disabled={busy}
          onClick={() => void onSave()}
        >
          ذخیره زمان‌بندی
        </Button>
        <Button
          variant="outline-secondary"
          disabled={busy}
          onClick={() => void onSkip()}
        >
          بعداً
        </Button>
      </Card.Footer>
    </Card>
  );
}
